// base
import { createAsyncThunk } from '@reduxjs/toolkit';

// api
import { projectApi } from '../apis/project.api';

// action
import { actions } from '../actions/project.action';

// models
import type {
  ProjectFile,
  ReqAddProjectImage,
  ReqDeleteProjectImage,
  ReqProjectInfo,
  ReqProjectModify,
  ResProjectInfo
} from '../models/project.model';
import type { ResultError } from 'modules/common';
import { ResMapRestriction } from 'containers/Dashboard/Map/Gs/GoogleMapTest';

/** 프로젝트 상세 조회 thunk 입니다. */
export const getProjectInfo = createAsyncThunk<
  ResProjectInfo,
  ReqProjectInfo,
  {
    rejectValue: ResultError;
  }
>(actions.GET_PROJECT_INFO, async (data, { fulfillWithValue, rejectWithValue }) => {
  try {
    const res = await projectApi.getProjectInfo(data);

    const { resultObject } = res;
    const { projectFiles } = resultObject;

    let projectFilesUrls: ProjectFile[] = [];

    if (projectFiles && projectFiles.length > 0) {
      projectFiles.forEach(async (file) => {
        projectFilesUrls.push({
          id: file.id,
          url: file.url
        });
      });
    }

    return fulfillWithValue({
      ...res,
      resultObject: {
        ...resultObject,
        projectFiles: projectFilesUrls
      }
    });
  } catch (error) {
    const err = error as ResultError;

    return rejectWithValue(err);
  }
});

/**
 * 프로젝트 업데이트 thunk 입니다.
 */
export const updateProject = createAsyncThunk<
  ResProjectInfo,
  ReqProjectModify,
  {
    rejectValue: ResultError;
  }
>(actions.UPDATE_PROJECT_INFO, async (data, { dispatch, fulfillWithValue, rejectWithValue }) => {
  try {
    const res = await projectApi.updateProject(data);

    const projectInfoAction = await dispatch(
      getProjectInfo({ projectId: res.resultObject.projectId })
    );
    const projectInfo = projectInfoAction.payload as ResProjectInfo;

    return fulfillWithValue({
      ...projectInfo
    });
  } catch (error) {
    const err = error as ResultError;

    return rejectWithValue(err);
  }
});

/**
 * 프로젝트 이미지 파일 추가 thunk 입니다.
 */
export const addProjectImage = createAsyncThunk<
  ResProjectInfo,
  ReqAddProjectImage,
  {
    rejectValue: ResultError;
  }
>(actions.ADD_PROJECT_IMAGE, async (data, { dispatch, fulfillWithValue, rejectWithValue }) => {
  try {
    await projectApi.addProjectImage(data);

    const projectInfoAction = await dispatch(getProjectInfo({ projectId: data.id }));

    const projectInfo = projectInfoAction.payload as ResProjectInfo;

    return fulfillWithValue({
      ...projectInfo
    });
  } catch (error) {
    const err = error as ResultError;

    return rejectWithValue(err);
  }
});

/**
 * 프로젝트 이미지 파일 삭제 thunk 입니다.
 */
export const deleteProjectImage = createAsyncThunk<
  ResProjectInfo,
  ReqDeleteProjectImage,
  {
    rejectValue: ResultError;
  }
>(actions.DELETE_PROJECT_IMAGE, async (data, { dispatch, fulfillWithValue, rejectWithValue }) => {
  try {
    await projectApi.deleteProjectImage(data);

    const projectInfoAction = await dispatch(getProjectInfo({ projectId: data.id }));

    const projectInfo = projectInfoAction.payload as ResProjectInfo;

    return fulfillWithValue({
      ...projectInfo
    });
  } catch (error) {
    const err = error as ResultError;

    return rejectWithValue(err);
  }
});

export const updateWorkArea = createAsyncThunk<
  ResProjectInfo,
  { projectId: string; data: ResMapRestriction },
  {
    rejectValue: ResultError;
  }
>(
  actions.UPDATE_WORK_AREA,
  async ({ projectId, data }, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await projectApi.updateWorkArea(projectId, data);

      const projectInfoAction = await dispatch(getProjectInfo({ projectId: projectId }));

      const projectInfo = projectInfoAction.payload as ResProjectInfo;

      return fulfillWithValue({ ...projectInfo });
    } catch (error) {
      const err = error as ResultError;

      return rejectWithValue(err);
    }
  }
);
